import { useEffect, useState, useMemo } from "react";
import { ReMixChart } from "../../../../../../components/recharts/ReMixCharts";
import { ChartType } from "../../../../../../models/chart_model";
import loader from "../../../../../../components/recharts/loader.gif";
import { StateMetricsResponse, getStateMetrics } from "../../../../../../Rest_api/RestApi";
import { renderHourTick } from "../../../../../../components/recharts/components";

interface ChartDataItem {
  name: string; // Time block
  drawal_mw: number;
  injection_mw: number;
  actual_drawal_mw: number;
  forecast_drawal_mw: number;
  temperature: number;
  rain: number;
  direct_radiation: number;
  [key: string]: any;
}

export interface GnaUtilisationVolumeAnylysisProps {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading: boolean;
  selectedTargetDate: any[];
  data: StateMetricsResponse | null;
  selectedDate?: string;
  availableDates?: any[];
  selectedState?: string;
  selectedExchanges?: string[];
  selectedMarkets?: string[];
}

export default function GnaUtilisationVolumeAnylysis({
  selectedBlock,
  refreshKey,
  setError,
  loading: parentLoading,
  selectedTargetDate,
  data: propData,
  selectedDate,
  availableDates,
  selectedState,
  selectedExchanges,
  selectedMarkets,
}: GnaUtilisationVolumeAnylysisProps) {

  // Local state for data fetching
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartKeys, setChartKeys] = useState<any[]>([]);

  // Default metrics to show
  const metricsList = [
    { key: "drawal_mw", label: "Time Block Drawal" },
    { key: "injection_mw", label: "Time Block Injection" },
    // Add other metrics as needed or make selectable
  ];

  useEffect(() => {
    if (!availableDates || availableDates.length === 0) {
      setChartData([]);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const dataMapByBlock = new Map<number, any>();

        // Initialize map with 96 blocks
        for (let i = 1; i <= 96; i++) {
          dataMapByBlock.set(i, { name: i.toString() });
        }

        const promises = availableDates.map(async (dateObj: any) => {
          const { value, label } = dateObj;
          const [startDate, endDate] = value.split(",");
          const safeLabel = label.replace(/[^a-zA-Z0-9]/g, "");

          // Fetch data for this range
          const state = selectedState || "UTTARPRADESH STATE";

          const response = await getStateMetrics(
            startDate,
            endDate || startDate,
            state,
            selectedExchanges,
            selectedMarkets
          );

          if (response && response.range) {
            Object.entries(response.range).forEach(([blockStr, metrics]: [string, any]) => {
              const block = parseInt(blockStr);
              const slotData = dataMapByBlock.get(block);
              if (slotData) {
                // Start filling metrics with suffix
                slotData[`drawal_mw_${safeLabel}`] = metrics.drawal_mw;
                slotData[`injection_mw_${safeLabel}`] = metrics.injection_mw;
                slotData[`actual_drawal_mw_${safeLabel}`] = metrics.actual_drawal_mw;
                slotData[`forecast_drawal_mw_${safeLabel}`] = metrics.forecast_drawal_mw;

                // Weather data (maybe we only need one set? Or average? Or plot all?)
                // For now, let's plot all with suffixes.
                slotData[`temperature_${safeLabel}`] = metrics.temperature;
                slotData[`rain_${safeLabel}`] = metrics.rain;
                slotData[`direct_radiation_${safeLabel}`] = metrics.direct_radiation;
                slotData[`humidity_${safeLabel}`] = metrics.humidity;
                slotData[`cloud_cover_${safeLabel}`] = metrics.cloud_cover;
              }
            });
          }
        });

        await Promise.all(promises);

        const pivotedData = Array.from(dataMapByBlock.values()).sort((a, b) => Number(a.name) - Number(b.name));
        setChartData(pivotedData);

        // keys
        const dynamicKeys = availableDates.map((dateObj: any) => ({
          label: dateObj.label,
          keySuffix: dateObj.label.replace(/[^a-zA-Z0-9]/g, "")
        }));
        setChartKeys(dynamicKeys);

      } catch (err) {
        console.error("Error fetching GNA Utilisation data", err);
        setError?.("Failed to fetch data");
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [availableDates, refreshKey, setError, selectedState, selectedExchanges, selectedMarkets]);

  const legends = useMemo(() => {
    const result: any[] = [];
    if (!chartKeys || chartKeys.length === 0) return [];

    // Define base metrics configuration
    const baseMetrics = [
      { key: "drawal_mw", name: "Time Block Drawal", type: ChartType.Line, unit: "MWh" },
      { key: "injection_mw", name: "Time Block Injection", type: ChartType.Line, unit: "MWh" },
      { key: "actual_drawal_mw", name: "Actual Consumption", type: ChartType.Line, unit: "MWh" },
      { key: "forecast_drawal_mw", name: "Forecasted Consumption", type: ChartType.Line, unit: "MWh" },
      // Weather metrics on right axis
      { key: "temperature", name: "Temperature", type: ChartType.Line, unit: "°C", yAxisId: "right" },
      { key: "rain", name: "Rain", type: ChartType.Line, unit: "mm", yAxisId: "right" },
      { key: "direct_radiation", name: "Solar Intensity", type: ChartType.Line, unit: "W/m²" }, // left axis?
      { key: "humidity", name: "Humidity", type: ChartType.Line, unit: "%", yAxisId: "right" },
      { key: "cloud_cover", name: "Cloud Cover", type: ChartType.Line, unit: "%", yAxisId: "right" }
    ];

    baseMetrics.forEach(metric => {
      chartKeys.forEach(dateKey => {
        result.push({
          dataKey: `${metric.key}_${dateKey.keySuffix}`,
          name: `${metric.name} (${dateKey.label})`,
          type: metric.type,
          unit: metric.unit,
          // stroke: metric.color, // Removed as we want auto colors
          // For now, user asked to "show more than one line for each thing", usually implies same color or distinct?
          // PowerInjectionChart uses fixed colors for metrics, but since we have multiple lines for same metric, 
          // usually we either use dashed lines for previous years or different shades.
          // However, I will stick to the requested "format" which handles keys.
          // If I reuse the same color for both lines, they will be indistinguishable.
          // But the user's ref file `PowerInjectionChart.tsx` uses `colorByPoint={false}` and `stroke: stroke`.
          // It seems `PowerInjectionChart` actually creates ONE line per metric per date range.
          // Wait, `PowerInjectionChart` legends logic:
          /*
           result.push({
            dataKey: dataKey,
            name: `${displayName} (${label})`,
            ...
            stroke: stroke, 
            isDash: isDash,
          });
          */
          // It uses the SAME stroke. This means multiple lines with same color? 
          // Unless Recharts handles it, or they are dashed?
          // Ah, `PowerInjectionChart` doesn't seem to vary color by date automatically in the code I saw, 
          // UNLESS `isDash` is used (which it is for Peak Demand).
          // I will leave the color as is for now.
          yAxisId: metric.yAxisId,
          isDash: false, // User requested simple lines
        });
      });
    });

    return result;
  }, [chartKeys]);

  return (
    <>
      {loading ? (
        <div className="w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center min-h-[300px]">
          <p className="text-gray-500">
            No data available for the selected date(s)
          </p>
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 50px)"
          data={chartData}
          legends={legends}
          brushHeight={15}
          xDataKey="name"
          isTimeSlot={true}
          yAxisLabel="MWh"
          secondYAxisLabel="Weather"
          secondYAxisDomain={[0, 150]}
          showBrush={true}
          showToolbox={false}
          top={30}
          bottom={30}
          xAxisInterval={"auto"}
          colorByPoint={false}
          xTick={(value: any) => renderHourTick(value, 4)}
          fontSize={10}
          isSmallLegend={true}
          xaxisHeight={50}
        />
      )}
    </>
  );
}
